/**
 * The Design tab's group tree: selection, nesting, and drag to reorder.
 *
 * The interaction follows the Scene Editor hierarchy: dragging starts from a
 * dedicated pointer grip, the source becomes a placeholder, and a fixed ghost
 * follows the pointer until the move is committed. Unlike Scene Editor, a
 * design needs no side-car order map: its nested `groups` arrays are the order.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { IconButton, Tooltip } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import AddBoxIcon from "@material-ui/icons/AddBox";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import FolderIcon from "@material-ui/icons/Folder";

/**
 * The Scene Editor's component glyph, drawn rather than imported.
 *
 * That tree loads `images/gameConfig/Inner-component-icon.svg` through webpack's
 * file-loader; this module is covered by a jest suite that has no such loader,
 * and a panel that cannot be required in a test is a panel with no tests. Same
 * 16x16 path, same stroke.
 */
function LeafGlyph() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      style={{ marginRight: 6, flexShrink: 0, opacity: 0.85 }}
    >
      <path
        d="M4.5 2H7.5M8.5 2H11.5M14 7.5V4.5M4.5 14H7.5M8.5 14H11.5M14 11.5V8.5M2 7.5V4.5M2 11.5V8.5M2 3.5V2H3.5"
        stroke="#949494"
      />
      <path d="M12.5 2H14V3.5" stroke="#949494" />
      <path d="M2 12.5V14H3.5" stroke="#949494" />
      <path d="M14 12.5V14H12.5" stroke="#949494" />
    </svg>
  );
}

/** How much of a row's height counts as "drop above" / "drop below". */
const EDGE_RATIO = 0.28;
/** `marginLeft: 9` + `paddingLeft: 6` — what one MUI TreeItem group level costs. */
const INDENT_PX = 15;

/**
 * Row chrome, matched to the Scene Editor hierarchy.
 *
 * That tree is a MUI SimpleTreeView and gets all of this from `TREE_SX`; this
 * one is a flat list of rows, because the drag code hit-tests every row as a
 * sibling. So the numbers are restated here rather than shared — the two trees
 * are meant to look identical and stay independent, which is the same bargain
 * `designFields.js` makes with the playEngine field chrome.
 */
const ROW_MIN_HEIGHT = 22;
const GUIDE_COLOR = "rgba(255, 255, 255, 0.09)";
const ROW_HOVER_BG = "rgba(255, 255, 255, 0.055)";
const ROW_SELECTED_BG = "rgba(144, 202, 249, 0.20)";
const ROW_SELECTED_HOVER_BG = "rgba(144, 202, 249, 0.26)";
const ROW_DROP_BG = "rgba(100, 181, 246, 0.22)";
const FOLDER_ICON_COLOR = "#adadad";

/**
 * The vertical rules that make nesting depth readable.
 *
 * The Scene Editor gets these for free: MUI nests each level in a `TreeItem`
 * group it can hang a `border-left` on. A flat list has no such wrapper, so the
 * same lines are drawn per row — one span for each level above this one, which
 * lands them in exactly the columns the nested version would have used.
 */
function IndentGuides({ depth }) {
  if (depth <= 0) return null;
  return Array.from({ length: depth }, (_unused, level) => (
    <span
      key={level}
      aria-hidden
      style={{
        flex: `0 0 ${INDENT_PX}px`,
        alignSelf: "stretch",
        borderLeft: `1px solid ${GUIDE_COLOR}`,
      }}
    />
  ));
}

/** Where in the row the pointer is, and therefore what a drop would mean. */
export function dropIntentFromPointer(clientY, rect) {
  if (!rect || rect.height <= 0) return "inside";
  const offset = (clientY - rect.top) / rect.height;
  if (offset < EDGE_RATIO) return "before";
  if (offset > 1 - EDGE_RATIO) return "after";
  return "inside";
}

function GroupRow({
  entry,
  selected,
  expanded,
  hasChildren,
  canEdit,
  isDragging,
  dropHint,
  onSelect,
  onToggle,
  onGripPointerDown,
  onAddChild,
  onRename,
  onRemove,
}) {
  const { group, depth } = entry;
  const [hovered, setHovered] = useState(false);
  const fieldCount = group.fields.length;
  // A group holding settings is deletable too — the panel asks before it takes
  // them, which is a better trade than emptying a scaffolded group row by row.
  const removable = canEdit;
  const holdsSettings = fieldCount > 0;

  if (isDragging) {
    return (
      <div
        data-design-group={group.id}
        style={{
          height: ROW_MIN_HEIGHT,
          marginLeft: 6 + depth * INDENT_PX,
          marginRight: 6,
          borderRadius: 2,
          border: "1px dashed rgba(144, 202, 249, 0.35)",
          backgroundColor: "rgba(144, 202, 249, 0.06)",
          boxSizing: "border-box",
        }}
        aria-hidden
      />
    );
  }

  // Hover is a background, not just a reveal of the row's buttons — without it
  // a flat list gives no feedback about which row a click would land on.
  const rowBackground =
    dropHint === "inside"
      ? ROW_DROP_BG
      : selected
        ? hovered
          ? ROW_SELECTED_HOVER_BG
          : ROW_SELECTED_BG
        : hovered
          ? ROW_HOVER_BG
          : "transparent";

  return (
    <div
      data-design-group={group.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(group.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onSelect(group.id);
      }}
      role="treeitem"
      aria-selected={selected}
      aria-expanded={hasChildren ? expanded : undefined}
      tabIndex={0}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        minHeight: ROW_MIN_HEIGHT,
        paddingRight: 6,
        paddingLeft: 6,
        cursor: "pointer",
        backgroundColor: rowBackground,
        boxShadow:
          dropHint === "before"
            ? "inset 0 2px 0 0 #64b5f6"
            : dropHint === "after"
              ? "inset 0 -2px 0 0 #64b5f6"
              : "none",
      }}
    >
      <IndentGuides depth={depth} />
      {/* Always 16px wide, occupied or not, so a leaf's icon lines up with a
          folder's — the job Scene Editor gives its `HierarchyLeafIcon`. It comes
          BEFORE the grip because that is the order MUI renders a TreeItem in:
          expand icon, then whatever the label draws. */}
      <span
        onClick={(event) => {
          event.stopPropagation();
          if (hasChildren) onToggle(group.id);
        }}
        style={{ width: 16, flexShrink: 0, display: "flex", alignItems: "center" }}
      >
        {hasChildren ? (
          expanded ? (
            <ArrowDropDownIcon style={{ fontSize: 18, color: "#8a8a8a" }} />
          ) : (
            <ArrowRightIcon style={{ fontSize: 18, color: "#8a8a8a" }} />
          )
        ) : null}
      </span>

      <span
        data-reorder-handle=""
        title={canEdit ? "Drag to reorder, or drop on a group to nest it" : undefined}
        onPointerDown={
          canEdit
            ? (event) => {
              event.stopPropagation();
              event.preventDefault();
              onGripPointerDown(event, entry);
            }
            : undefined
        }
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 14,
          height: 16,
          marginRight: 2,
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: "none",
          cursor: canEdit ? "grab" : "default",
          color: "#777",
          visibility: canEdit ? "visible" : "hidden",
        }}
      >
        <DragIndicatorIcon style={{ fontSize: 13 }} />
      </span>

      {/* A group holding groups reads as a folder; one holding settings reads as
          a leaf — the same two icons, and the same two weights below, that the
          Scene Editor uses to separate a category from a component. */}
      {hasChildren ? (
        <FolderIcon
          style={{ fontSize: 15, marginRight: 6, color: FOLDER_ICON_COLOR, flexShrink: 0 }}
        />
      ) : (
        <LeafGlyph />
      )}

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: hasChildren ? 500 : 600,
          color: hasChildren ? "#d6d6d6" : "#e0e0e0",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {group.label}
      </span>

      {/* How many settings sit in this group. Left of the buttons rather than
          instead of them: a row that swapped one for the other on hover changed
          width under the pointer. */}
      <span style={{ fontSize: 10, color: "#7f838a", flexShrink: 0, marginLeft: 4 }}>
        {fieldCount || ""}
      </span>

      {/* Shown whether or not the row is hovered — the Scene Editor keeps its
          category actions on the row at all times, and a hidden control is one
          nobody finds. */}
      {canEdit ? (
        <span className="proto-cat-actions" style={{ display: "flex", flexShrink: 0, marginLeft: 4 }}>
          <Tooltip title="Add a group inside this one" arrow>
            <IconButton
              size="small"
              style={{ padding: 2, color: "#8a8d93" }}
              onClick={(event) => {
                event.stopPropagation();
                onAddChild(group.id);
              }}
            >
              <AddBoxIcon style={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rename" arrow>
            <IconButton
              size="small"
              style={{ padding: 2, color: "#8a8d93" }}
              onClick={(event) => {
                event.stopPropagation();
                onRename(group.id, group.label);
              }}
            >
              <EditIcon style={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              holdsSettings
                ? "Delete this group and everything in it"
                : "Delete this group"
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                disabled={!removable}
                style={{ padding: 2, color: removable ? "#8a8d93" : "#4a4d53" }}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(group.id);
                }}
              >
                <DeleteIcon style={{ fontSize: 12 }} />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array} props.entries walkGroups output, filtered to visible entries
 * @param {string|null} props.selectedId
 * @param {Set<string>} props.expandedIds
 * @param {boolean} props.canEdit
 * @param {(dragId: string, targetId: string, position: string) => void} props.onMove
 */
export default function DesignOutliner({
  entries,
  selectedId,
  expandedIds,
  canEdit,
  onSelect,
  onToggle,
  onMove,
  onAddChild,
  onRename,
  onRemove,
}) {
  const [dragSession, setDragSession] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const dragSessionRef = useRef(null);
  const dropTargetRef = useRef(null);
  const dragGhostRef = useRef(null);

  const entriesById = useMemo(
    () => new Map(entries.map((entry) => [entry.group.id, entry])),
    [entries]
  );

  function setCurrentDropTarget(next) {
    dropTargetRef.current = next;
    setDropTarget((current) =>
      current?.id === next?.id && current?.position === next?.position ? current : next
    );
  }

  function resolveDropTarget(clientX, clientY, draggedId) {
    const element = document.elementFromPoint(clientX, clientY);
    const row = element?.closest("[data-design-group]");
    if (!row) return null;

    const id = row.getAttribute("data-design-group");
    const entry = entriesById.get(id);
    if (!entry || id === draggedId || entry.ancestors.includes(draggedId)) return null;

    return {
      id,
      position: dropIntentFromPointer(clientY, row.getBoundingClientRect()),
    };
  }

  function beginDrag(pointerEvent, entry) {
    if (!canEdit) return;
    const row = pointerEvent.currentTarget.closest("[data-design-group]");
    if (!row) return;
    const rect = row.getBoundingClientRect();

    try {
      pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    } catch (_err) {
      // Pointer capture is an enhancement; window listeners still own the drag.
    }

    const session = {
      draggedId: entry.group.id,
      caption: entry.group.label,
      pointerId: pointerEvent.pointerId,
      captureTarget: pointerEvent.currentTarget,
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetY: pointerEvent.clientY - rect.top,
      floatTop: rect.top,
      floatLeft: rect.left,
      floatWidth: rect.width,
      slotHeight: Math.max(26, Math.round(rect.height)),
    };
    dragSessionRef.current = session;
    setCurrentDropTarget(null);
    setDragSession(session);
  }

  useEffect(() => {
    if (!dragSession) return undefined;

    function onPointerMove(event) {
      const session = dragSessionRef.current;
      if (!session) return;

      const next = {
        ...session,
        pointerX: event.clientX,
        pointerY: event.clientY,
        floatTop: event.clientY - session.grabOffsetY,
      };
      dragSessionRef.current = next;
      setCurrentDropTarget(resolveDropTarget(event.clientX, event.clientY, session.draggedId));
      setDragSession(next);

      if (dragGhostRef.current) dragGhostRef.current.style.top = `${next.floatTop}px`;
    }

    function finishDrag() {
      const session = dragSessionRef.current;
      const target = dropTargetRef.current;
      if (!session) return;

      if (session.captureTarget?.releasePointerCapture) {
        try {
          session.captureTarget.releasePointerCapture(session.pointerId);
        } catch (_err) {
          // The browser may already have released it after pointercancel.
        }
      }

      dragSessionRef.current = null;
      setCurrentDropTarget(null);
      setDragSession(null);
      if (target) onMove(session.draggedId, target.id, target.position);
    }

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", finishDrag);
    window.addEventListener("pointercancel", finishDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", finishDrag);
      window.removeEventListener("pointercancel", finishDrag);
    };
  }, [dragSession?.draggedId, entriesById, onMove]);

  // When a parent moves, its open descendants travel with it; do not leave
  // those child rows behind under the placeholder during the drag.
  const renderedEntries = dragSession
    ? entries.filter(
      (entry) =>
        entry.group.id === dragSession.draggedId ||
        !entry.ancestors.includes(dragSession.draggedId)
    )
    : entries;

  return (
    <>
      <div role="tree" data-playengine-sort-list="design-groups" style={{ paddingBottom: 4 }}>
        {renderedEntries.map((entry) => (
          <GroupRow
            key={entry.group.id}
            entry={entry}
            selected={entry.group.id === selectedId}
            expanded={expandedIds.has(entry.group.id)}
            hasChildren={entry.group.groups.length > 0}
            canEdit={canEdit && !dragSession}
            isDragging={dragSession?.draggedId === entry.group.id}
            dropHint={dropTarget?.id === entry.group.id ? dropTarget.position : null}
            onSelect={onSelect}
            onToggle={onToggle}
            onGripPointerDown={beginDrag}
            onAddChild={onAddChild}
            onRename={onRename}
            onRemove={onRemove}
          />
        ))}
      </div>

      {dragSession && typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={dragGhostRef}
            style={{
              position: "fixed",
              top: dragSession.floatTop,
              left: dragSession.floatLeft,
              width: dragSession.floatWidth,
              height: dragSession.slotHeight,
              zIndex: 10000,
              pointerEvents: "none",
              borderRadius: 2,
              backgroundColor: "#3a3a41",
              border: "1px solid rgba(144, 202, 249, 0.55)",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              padding: "0 6px",
            }}
          >
            <DragIndicatorIcon style={{ fontSize: 14, color: "#90caf9", marginRight: 2, flexShrink: 0 }} />
            <LeafGlyph />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#e0e0e0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {dragSession.caption}
            </span>
          </div>,
          document.body
        )}
    </>
  );
}
